import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { localiseMeasurement } from '@/utils/unitSystem';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { BlisterFieldsData } from '../hooks/useBlisterData';
import { ErrorChip } from './styles';
import {
  UNCERTAIN_PROFILE_SHORT_LABEL,
  ESTIMATED_BLISTER_HEIGHT_LABEL,
  ESTIMATED_PIT_DEPTH_LABEL,
  PIT_TYPE,
  BLISTER_TYPE,
} from '@/constants';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { useAtom } from 'jotai';
import { convertTemplateNameToPoiType } from '@/utils';
import { UncertainFieldHook } from '../hooks/useUncertainField';
import { UncertainReasonField } from './UncertainReasonField';
import { ManualHeightField, MarkAsPitCheckbox } from './BlisterFormParts';

type BlisterFormFieldsProps = {
  blisterData: BlisterFieldsData | undefined;
  handleManualHeightChange: (height: string) => void;
  handleMeasureIconClick: () => void;
  onFieldChange?: (value: boolean) => void;
  allDisabled?: boolean;
  uncertainField: UncertainFieldHook;
};

export const BlisterFormFields = ({
  blisterData,
  handleManualHeightChange,
  handleMeasureIconClick,
  onFieldChange,
  uncertainField,
  allDisabled,
}: BlisterFormFieldsProps) => {
  const unitSystem = useAtomValue(unitSystemState);
  const [editPointOfInterest] = useAtom(poiState.editPointOfInterest);

  const template = editPointOfInterest?.template;
  const poiType = convertTemplateNameToPoiType(template?.key ?? template?.name);

  const formatValue = useCallback(
    (value: string | undefined) => {
      if (!value) return '';
      return (
        localiseMeasurement({
          unitSystem,
          value: Number(value),
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          roundToDecimal: 3,
        }) ?? ''
      ).replace(/[^\d.]+$/, '');
    },
    [unitSystem]
  );

  const unit = useMemo(() => {
    return unitSystem === UnitSystemEnum.Imperial ? '"' : 'mm';
  }, [unitSystem]);

  const blisterHeight = useMemo(() => {
    if (!blisterData) return undefined;
    return formatValue(blisterData.estimatedHeight);
  }, [blisterData, formatValue]);

  useEffect(() => {
    if (blisterHeight) uncertainField.handleUpdateReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blisterHeight]);

  const estimatedHeightColor = useMemo(() => {
    if (blisterData?.manualSetHeight) return '#b0b3b7';
    return 'inherit';
  }, [blisterData?.manualSetHeight]);

  const estimatedHeightLabel = useMemo(() => {
    switch (poiType) {
      case BLISTER_TYPE:
        return ESTIMATED_BLISTER_HEIGHT_LABEL;
      case PIT_TYPE:
        return ESTIMATED_PIT_DEPTH_LABEL;
      default:
        return '';
    }
  }, [poiType]);

  return (
    <Stack spacing={3} sx={{ width: '100%', marginTop: 3.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ color: estimatedHeightColor }}
      >
        <Typography sx={{ fontSize: '1.4rem' }}>{estimatedHeightLabel}</Typography>
        {blisterHeight ? (
          <Typography sx={{ fontWeight: 600, fontSize: '1.6rem' }}>
            {blisterHeight + unit}
          </Typography>
        ) : (
          <ErrorChip>{UNCERTAIN_PROFILE_SHORT_LABEL}</ErrorChip>
        )}
      </Stack>
      <ManualHeightField
        manualSetHeight={blisterData?.manualSetHeight}
        handleManualHeightChange={handleManualHeightChange}
        handleMeasureIconClick={handleMeasureIconClick}
        disabled={allDisabled}
      />
      {blisterHeight && <MarkAsPitCheckbox onFieldChange={onFieldChange} disabled={allDisabled} />}
      {uncertainField.fieldValue && (
        <UncertainReasonField uncertainField={uncertainField} disabled={allDisabled} />
      )}
    </Stack>
  );
};
